'use client'

import { useTranslations } from 'next-intl'
import { useEffect } from 'react'
import { formatUnits } from 'viem'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { type UpgradeState, useBrokenSafeUpgrade } from '@/hooks/use-broken-safe-upgrade'
import { useWallet } from '@/providers/WalletContext'

interface Props {
	mode: 'forced' | 'manual'
	open: boolean
	onClose?: () => void
}

function stateLabel(state: UpgradeState, t: ReturnType<typeof useTranslations>): string {
	switch (state) {
		case 'checking':
			return t('upgradeWallet.checkingBalance')
		case 'deploying':
			return t('upgradeWallet.stateDeploying')
		case 'awaiting-signature':
			return t('upgradeWallet.stateAwaitingSignature')
		case 'submitting':
			return t('upgradeWallet.stateSubmitting')
		case 'verifying':
			return t('upgradeWallet.stateVerifying')
		case 'success':
			return t('upgradeWallet.stateSuccess')
		default:
			return ''
	}
}

export function UpgradeBrokenSafeModal({ mode, open, onClose }: Props) {
	const t = useTranslations()
	const { userId, eoaAddress, walletClient, publicClient } = useWallet()
	const upgrade = useBrokenSafeUpgrade({
		userId,
		eoaAddress,
		walletClient,
		publicClient,
	})

	// On success, refresh window after short delay so the entire app re-bootstraps
	// against the deposit wallet path (manual mode just closes the dialog).
	useEffect(() => {
		if (upgrade.state !== 'success') return
		const id = setTimeout(() => {
			if (mode === 'manual' && onClose) onClose()
			else window.location.reload()
		}, 1500)
		return () => clearTimeout(id)
	}, [upgrade.state, mode, onClose])

	const isWorking =
		upgrade.state === 'checking' ||
		upgrade.state === 'deploying' ||
		upgrade.state === 'awaiting-signature' ||
		upgrade.state === 'submitting' ||
		upgrade.state === 'verifying'

	const stuckTotal = (upgrade.usdceBalance ?? 0n) + (upgrade.pusdBalance ?? 0n)

	return (
		<Dialog
			open={open}
			onOpenChange={next => {
				if (mode === 'forced') return
				if (!next && onClose) onClose()
			}}
		>
			<DialogContent
				className="max-w-md"
				showCloseButton={mode !== 'forced'}
				onEscapeKeyDown={e => {
					if (mode === 'forced') e.preventDefault()
				}}
				onPointerDownOutside={e => {
					if (mode === 'forced') e.preventDefault()
				}}
				onInteractOutside={e => {
					if (mode === 'forced') e.preventDefault()
				}}
			>
				<DialogTitle>{t('upgradeWallet.title')}</DialogTitle>
				<p className="text-brand-muted text-sm">{t('upgradeWallet.subtitle')}</p>

				{(upgrade.usdceBalance !== null || upgrade.pusdBalance !== null) && (
					<div className="rounded border border-brand-gray bg-brand-dark-light p-3">
						<div className="text-brand-muted text-xs">{t('upgradeWallet.stuckBalanceLabel')}</div>
						<div className="mt-1 text-lg">
							{Number.parseFloat(formatUnits(stuckTotal, 6)).toFixed(2)} USD
						</div>
					</div>
				)}

				{isWorking && <p className="text-brand-muted text-sm">{stateLabel(upgrade.state, t)}</p>}

				{upgrade.state === 'error' && (
					<p className="text-brand-red text-sm">
						{upgrade.error ?? t('upgradeWallet.errorGeneric')}
					</p>
				)}

				{upgrade.state === 'success' ? (
					<p className="text-brand-green text-sm">{t('upgradeWallet.stateSuccess')}</p>
				) : (
					<Button
						onClick={() => {
							upgrade.upgrade().catch(() => {
								// errors are surfaced through upgrade.error / state === 'error'
							})
						}}
						disabled={isWorking}
						className="w-full"
					>
						{upgrade.state === 'error'
							? t('upgradeWallet.buttonRetry')
							: t('upgradeWallet.buttonUpgrade')}
					</Button>
				)}
			</DialogContent>
		</Dialog>
	)
}
